import useCheckoutState from './useCheckoutState';
import getBulkDiscount from '../lib/getBulkDiscount';
import getSetupFeeAmount from '../lib/getSetupFeeAmount';
import getTotalDueAmount from '../lib/getTotalDueAmount';
import getOneTimeTotalAmount from '../lib/getOneTimeTotalAmount';
import useTaxCalculation from './useTaxCalculation';
import getSubtotalAmount from '../lib/getSubtotalAmount';
import getRecurringTotalAmount from '../lib/getRecurringTotalAmount';
import useProductPrice from './useProductPrice';
import useQuantity from './useQuantity';
import React from 'react';
import getSelectedVariants from '../lib/getSelectedVariants';
import getOneTimeVariantsAmount from '../lib/getOneTimeVariantsAmount';
import getSubscriptionVariantsAmount from '../lib/getSubscriptionVariantsAmount';
import getOnetimeSubtotalAmount from '../lib/getOneTimeSubtotalAmount';
import getSelectedTicketTypes from '../lib/getSelectedTicketTypes';
import getTotalTicketTypesAmount from '../lib/getTotalTicketTypesAmount';
import { PAGE_TYPES } from '../enums/page-types';
import { productTypes } from '../enums/product-types';
import getPlanIterations from '../lib/getPlanIterations';
import getFees from '../lib/getFees';
import { getProportionateDiscountAmounts } from '../lib/getProportionateDiscountAmount';
import getTotalTicketQuantity from '../lib/getTotalTicketQuantity';
import getTotalPaidTicketQuantity from '../lib/getTotalPaidTicketQuantity';
import { splitBulkDiscounts } from '../lib/splitBulkDiscounts';
import { getPartialAmount } from '../lib/getPartialAmount';
import useProration from './useProration';
import { isProduction } from '../services/env';

const CartCostContext = React.createContext();

export function checkIsTaxEnabled({ seller, page }) {
  if (seller?.tax?.enabled && page?.tax?.enabled) {
    return true;
  }

  return false;
}

function getMode({ page, product }) {
  if (page.type === PAGE_TYPES.CHECKOUT) {
    if (product.type === productTypes.SUBSCRIPTION) {
      return 'subscription';
    } else if (product.type === productTypes.CHARGE) {
      return 'payment';
    }
  } else if (page.type === PAGE_TYPES.EVENT) {
    return 'payment';
  }

  return '';
}

function getCurrency(
  { page, product } = {
    page: {},
    product: {},
  },
) {
  if (page.type === PAGE_TYPES.CHECKOUT) {
    return product.currency;
  } else if (page.type === PAGE_TYPES.EVENT) {
    return page.eventDetails.currency;
  }

  return '';
}

function CartCostProvider({ children }) {
  const {
    form,
    page,
    product,
    coupon,
    payWhatYouWantPrice,
    seller,
    dynamicPlanIterations,
    selectedPaymentMethod,
    paymentOption,
  } = useCheckoutState();

  if (page.type === PAGE_TYPES.FORM) return children;

  const taxEnabled = checkIsTaxEnabled({ seller, page });

  const {
    recurringTaxAmount,
    recurringAmountExcludingTax,
    recurringTaxAmounts,
    recurringTaxAmountBeforeProration,
    recurringAmountExcludingTaxBeforeProration,
    recurringTaxAmountsBeforeProration,
    oneTimeTaxAmount,
    oneTimeTotalAmountExcludingTax,
    oneTimeTotalIncludingTax,
    oneTimeTaxAmounts,
    totalTaxAmounts,
    totalTaxAmountsBeforeProration,
    isCalculatingTax,
  } = useTaxCalculation(taxEnabled);

  const hasTax =
    taxEnabled &&
    (recurringAmountExcludingTax > 0 ||
      oneTimeTotalAmountExcludingTax > 0 ||
      oneTimeTotalIncludingTax > 0);

  const { proration, isRefetchingProration, isLoadingProration, hasProration } = useProration();

  const productPriceAmount = useProductPrice();

  const quantity = useQuantity();

  const bulkDiscounts =
    page.type === PAGE_TYPES.EVENT ? page.eventDetails.discounts : product.discounts;

  // Split bulk discounts into three categories
  const { subtotalDiscounts, variantDiscounts, ticketTypeDiscounts } =
    splitBulkDiscounts(bulkDiscounts);

  const selectedVariants = getSelectedVariants(product.variants, form.variants);

  const selectedTicketTypes = getSelectedTicketTypes(page.ticketGroups, form.ticketGroups);
  const totalTicketQuantity = getTotalTicketQuantity(selectedTicketTypes);
  const totalPaidTicketQuantity = getTotalPaidTicketQuantity(selectedTicketTypes);

  const totalTicketTypes = getTotalTicketTypesAmount(selectedTicketTypes, ticketTypeDiscounts);
  const totalTicketTypesAmount = totalTicketTypes.totalTicketTypesAmount;
  const totalTicketBulkTypesDiscountAmount = totalTicketTypes.totalTicketTypesDiscountAmount;

  let oneTimeVariantsAmount = 0;
  let oneTimeVariantsBulkDiscountAmount = 0;
  let subscriptionVariantsAmount = 0;

  const isCharge = product.type === 'charge';
  const isSubscription = product.type === 'subscription';
  const isEvent = page.type === 'event';

  if (isCharge) {
    const oneTimeVariants = getOneTimeVariantsAmount(
      selectedVariants,
      quantity,
      'charge',
      variantDiscounts,
    );

    oneTimeVariantsAmount = oneTimeVariants.oneTimeVariantsAmount;
    oneTimeVariantsBulkDiscountAmount = oneTimeVariants.oneTimeVariantsDiscountAmount;
  } else if (isSubscription) {
    const oneTimeVariants = getOneTimeVariantsAmount(
      selectedVariants,
      quantity,
      'subscription',
      variantDiscounts,
    );

    oneTimeVariantsAmount = oneTimeVariants.oneTimeVariantsAmount;
    oneTimeVariantsBulkDiscountAmount = oneTimeVariants.oneTimeVariantsDiscountAmount;

    subscriptionVariantsAmount = getSubscriptionVariantsAmount(selectedVariants, quantity);
  }

  const prorationAmount = hasProration ? proration?.amount : 0;

  const singleIterationAmount = productPriceAmount * quantity + subscriptionVariantsAmount;

  const planIterations = getPlanIterations({
    productPlanIterations: product.planIterations,
    allowDynamicPlanIterations: page.allowDynamicPlanIterations,
    dynamicPlanIterations,
  });

  // Calculate subtotal before applying any discounts
  const subtotalAmount = getSubtotalAmount({
    productPriceAmount: productPriceAmount,
    totalTicketTypesAmount: totalTicketTypesAmount,
    quantity,
    oneTimeVariantsAmount,
    subscriptionVariantsAmount,
    payWhatYouWantPrice: product.payWhatYouWant && Number(payWhatYouWantPrice),
    productType: product.type,
    planIterations: planIterations,
  });

  const bulkDiscountQuantity = page.type === PAGE_TYPES.EVENT ? totalPaidTicketQuantity : quantity;

  // Apply ticket type & variant option bulk discounts to subtotal (excluding variants)
  // to calculate the subtotal bulk discount
  let subtotalAfterItemDiscounts;
  if (page.type === PAGE_TYPES.EVENT) {
    subtotalAfterItemDiscounts = subtotalAmount - totalTicketBulkTypesDiscountAmount;
  } else {
    subtotalAfterItemDiscounts = subtotalAmount - oneTimeVariantsBulkDiscountAmount;
  }

  // Apply bulk discounts to subtotal (excluding variants)
  const { bulkDiscount: subtotalBulkDiscount, bulkDiscountAmount: subtotalBulkDiscountAmount } =
    getBulkDiscount(subtotalDiscounts, bulkDiscountQuantity, subtotalAfterItemDiscounts);

  let setupFeeAmount;
  if (isSubscription) {
    setupFeeAmount = getSetupFeeAmount(product.setupFee);
  } else {
    setupFeeAmount = getSetupFeeAmount(
      product.setupFee,
      quantity,
      product.setupFeeMultipliesWithQuantity,
    );
  }

  let recurringSubtotalAmount = 0;
  if (isSubscription) {
    recurringSubtotalAmount = productPriceAmount * quantity + subscriptionVariantsAmount;
  }

  const totalBulkDiscountAmount =
    subtotalBulkDiscountAmount +
    oneTimeVariantsBulkDiscountAmount +
    totalTicketBulkTypesDiscountAmount;

  const oneTimeSubtotalAmount = getOnetimeSubtotalAmount({
    productType: product.type,
    productPriceAmount,
    totalTicketTypesAmount,
    quantity,
    oneTimeVariantsAmount,
    payWhatYouWantPrice,
    bulkDiscountAmount: totalBulkDiscountAmount,
  });

  // get proportionate discount amounts
  const {
    couponDiscountAmount,
    recurringDiscountAmount,
    oneTimeVariantsDiscountAmount,
    setupFeeDiscountAmount,
  } = getProportionateDiscountAmounts({
    recurringSubtotalAmount,
    oneTimeSubtotalAmount,
    setupFeeAmount,
    coupon,
    startDate: product.startDate,
    trialPeriodDays: product.trialPeriodDays,
  });

  let recurringTotalAmountBeforeProration = 0;
  let recurringTotalAmount = 0;
  if (isSubscription) {
    recurringTotalAmountBeforeProration = getRecurringTotalAmount({
      recurringAmountExcludingTax: recurringAmountExcludingTaxBeforeProration,
      recurringTaxAmount: recurringTaxAmountBeforeProration,
      recurringSubtotalAmount: singleIterationAmount,
    });

    recurringTotalAmount = getRecurringTotalAmount({
      recurringAmountExcludingTax,
      recurringTaxAmount,
      recurringSubtotalAmount: singleIterationAmount,
    });
  }

  const oneTimeTotalAmount = getOneTimeTotalAmount({
    productType: product.type,
    oneTimeTaxAmount,
    oneTimeTotalAmountExcludingTax,
    oneTimeSubtotalAmount,
    oneTimeVariantsAmount,
    setupFeeAmount,
    hasTax,
  });

  let totalDiscountAmount = 0;
  if (isSubscription) {
    totalDiscountAmount = couponDiscountAmount;
  } else {
    totalDiscountAmount = totalBulkDiscountAmount + couponDiscountAmount;
  }

  const currency = getCurrency({ page, product });
  const mode = getMode({ page, product });

  // get list of fees that are based on the one time amount and recurring amount (single iteration)
  let fees;
  let totalAmount;
  let totalDueAmountBeforeProration;
  let totalDueAmount;

  if (isSubscription && hasTax) {
    // Subscription with Stripe Tax
    const recurringTotalAmountBeforeTax = getRecurringTotalAmount({
      recurringSubtotalAmount: singleIterationAmount,
      totalDiscountAmount: recurringDiscountAmount,
    });

    const oneTimeTotalAmountBeforeTax = getOneTimeTotalAmount({
      productType: product.type,
      oneTimeSubtotalAmount,
      oneTimeVariantsAmount,
      setupFeeAmount,
    });

    fees = getFees({
      fees: page.fees,
      amount: recurringTotalAmountBeforeTax + oneTimeTotalAmountBeforeTax - couponDiscountAmount,
      pageType: page.type,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    if (hasProration) {
      totalDueAmountBeforeProration = getTotalDueAmount({
        productType: product.type,
        oneTimeTotalAmount,
        recurringTotalAmount: recurringTotalAmountBeforeProration,
        startDate: product?.startDate,
        trialPeriodDays: product?.trialPeriodDays,
      });

      totalDueAmount = getTotalDueAmount({
        productType: product.type,
        oneTimeTotalAmount,
        recurringTotalAmount,
        startDate: product?.startDate,
        trialPeriodDays: product?.trialPeriodDays,
      });
    } else {
      // calculate without fees as they're already included in the totals
      totalDueAmount = getTotalDueAmount({
        productType: product.type,
        oneTimeTotalAmount,
        recurringTotalAmount,
        startDate: product?.startDate,
        trialPeriodDays: product?.trialPeriodDays,
      });
    }

    // not entirely sure if this is accurate for subscriptions
    totalAmount = getTotalDueAmount({
      productType: product.type,
      oneTimeTotalAmount,
      recurringTotalAmount,
      fees,
    });
  } else if (isSubscription) {
    // Subscription without Stripe Tax
    let totalAmount = Math.max(0, oneTimeTotalAmount + recurringTotalAmount - couponDiscountAmount);

    const oneTimeFees = getFees({
      fees: page.fees?.filter(fee => fee.percentage),
      amount: oneTimeTotalAmount - (oneTimeVariantsDiscountAmount + setupFeeDiscountAmount),
      pageType: page.type,
      numberOfTickets: totalPaidTicketQuantity,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    fees = getFees({
      fees: page.fees,
      amount: totalAmount,
      pageType: page.type,
      numberOfTickets: totalPaidTicketQuantity,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    let oneTimeDiscountAmount;
    if (isSubscription) {
      oneTimeDiscountAmount = oneTimeVariantsDiscountAmount + setupFeeDiscountAmount;
    } else {
      oneTimeDiscountAmount = couponDiscountAmount;
    }

    if (hasProration) {
      totalDueAmountBeforeProration = getTotalDueAmount({
        productType: product.type,
        oneTimeTotalAmount: Math.max(0, oneTimeTotalAmount - oneTimeDiscountAmount),
        recurringTotalAmount: Math.max(0, recurringTotalAmount - recurringDiscountAmount),
        oneTimeFees,
        fees,
        startDate: product?.startDate,
        trialPeriodDays: product?.trialPeriodDays,
      });

      totalDueAmount = getTotalDueAmount({
        productType: product.type,
        oneTimeTotalAmount: Math.max(0, oneTimeTotalAmount - oneTimeDiscountAmount),
        recurringTotalAmount: Math.max(0, prorationAmount), // recurring discount is already included in the proration amount
        oneTimeFees,
        startDate: product?.startDate,
        trialPeriodDays: product?.trialPeriodDays,
      });
    } else {
      totalDueAmount = getTotalDueAmount({
        productType: product.type,
        oneTimeTotalAmount: Math.max(0, oneTimeTotalAmount - oneTimeDiscountAmount),
        recurringTotalAmount: Math.max(0, recurringTotalAmount - recurringDiscountAmount),
        oneTimeFees,
        fees,
        startDate: product?.startDate,
        trialPeriodDays: product?.trialPeriodDays,
      });
    }

    // not entirely sure if this is accurate for subscriptions
    totalAmount = getTotalDueAmount({
      productType: product.type,
      oneTimeTotalAmount: Math.max(0, oneTimeTotalAmount - oneTimeDiscountAmount),
      recurringTotalAmount: Math.max(0, recurringTotalAmount - recurringDiscountAmount),
      fees,
    });

    // remove the recurring discount from the recurring total amount
    recurringTotalAmount = Math.max(0, recurringTotalAmount - recurringDiscountAmount);
  } else if ((isCharge || isEvent) && hasTax) {
    // One-time payment with Stripe Tax

    // get the one time total amount before tax to calculate the fees to display
    // prevents the fees from being recalculated with tax
    const oneTimeTotalAmountBeforeTax = getOneTimeTotalAmount({
      productType: product.type,
      oneTimeSubtotalAmount,
      setupFeeAmount,
    });

    // calculate the fees to display
    fees = getFees({
      fees: page.fees,
      amount: Math.max(0, oneTimeTotalAmountBeforeTax - couponDiscountAmount),
      numberOfTickets: totalPaidTicketQuantity,
      pageType: page.type,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    // fees have already been calculated, so we can use the one time total amount including tax
    totalDueAmount = getTotalDueAmount({
      productType: 'charge',
      oneTimeTotalAmount: Math.max(0, oneTimeTotalIncludingTax),
    });

    totalAmount = getTotalDueAmount({
      productType: product.type,
      oneTimeTotalAmount: Math.max(0, oneTimeTotalAmount),
      fees,
    });
  } else if (isCharge || isEvent) {
    // One-time payment
    let totalAmount = Math.max(0, oneTimeTotalAmount - couponDiscountAmount); // bulk discount amount has already been deducted from oneTimeTotalAmount

    fees = getFees({
      fees: page.fees,
      amount: totalAmount,
      pageType: page.type,
      numberOfTickets: totalPaidTicketQuantity,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    totalDueAmount = getTotalDueAmount({
      productType: product.type,
      oneTimeTotalAmount: Math.max(0, oneTimeTotalAmount - couponDiscountAmount),
      fees,
    });

    totalAmount = getTotalDueAmount({
      productType: product.type,
      oneTimeTotalAmount: Math.max(0, oneTimeTotalAmount),
      fees,
    });
  }

  // After calculating the initial totalDueAmount, add this code:
  if (paymentOption.type === 'partial') {
    const partialAmount = getPartialAmount(totalDueAmount, paymentOption.partialAmount);
    totalDueAmount = partialAmount;
  }

  const cartCost = {
    currency,
    mode,

    // tickets
    selectedTicketTypes,
    totalTicketQuantity,
    totalPaidTicketQuantity,

    // product
    productPriceAmount, // The (dynamic) price of the main product, excluding variants
    planIterations, // The (dynamic) number of iterations for the product

    // product, variants & quantity
    subtotalAmount, // The subtotal, excluding setup fee, excluding discounts, before taxes, shipping, and other adjustments
    oneTimeSubtotalAmount, // The subtotal for one-time items, excluding setup fee, excluding discounts, before taxes, shipping, and other adjustments
    recurringSubtotalAmount, // The subtotal for recurring items, excluding setup fee, excluding discounts, before taxes, shipping, and other adjustments

    shippingAmount: 0, // The shipping amount

    // discounts
    subtotalBulkDiscount,
    totalBulkDiscountAmount,
    coupon,
    couponDiscountAmount: couponDiscountAmount,
    totalDiscountAmount: totalDiscountAmount,
    // setup fee
    setupFeeAmount, // The setup fee

    // recurring tax
    recurringTaxAmount,
    recurringAmountExcludingTax,
    recurringTaxAmounts,

    // recurring tax before proration
    recurringTaxAmountBeforeProration,
    recurringAmountExcludingTaxBeforeProration,
    recurringTaxAmountsBeforeProration,

    // one-time tax
    oneTimeTaxAmount,
    oneTimeTotalAmountExcludingTax,
    oneTimeTaxAmounts,

    // total tax (combined)
    totalTaxAmounts,
    totalTaxAmountsBeforeProration,

    isCalculatingTax,
    isLoadingProration,
    isRefetchingProration,
    hasProration,
    isLoading: isCalculatingTax || isRefetchingProration,

    // fees
    fees,

    // total amount including all fees and taxes, before discounts
    totalAmount,

    // totals
    oneTimeTotalAmount, // The total one-time amount, not including setup fee
    recurringTotalAmount, // The total recurring amount, not including setup fee
    totalDueAmountBeforeProration,
    totalDueAmount, // The total amount due now after all discounts, taxes, shipping, and other adjustments
  };

  // only attach to window on client
  if (!isProduction && typeof window !== 'undefined') {
    window.cart = cartCost;
  }

  return <CartCostContext.Provider value={cartCost}>{children}</CartCostContext.Provider>;
}

export default function useCartCost() {
  const { page } = useCheckoutState();

  if (page.type === PAGE_TYPES.FORM) return {};

  const context = React.useContext(CartCostContext);

  if (context === undefined) {
    throw new Error('useCartCost must be used within a CartCostProvider');
  }

  return context;
}

export { CartCostProvider };
